/**
 * Error severity levels for categorizing and prioritizing errors.
 */
export enum EErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

/**
 * Error category for grouping errors by domain.
 */
export enum EErrorCategory {
  NETWORK = 'network',
  API = 'api',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  TIMEOUT = 'timeout',
  RATE_LIMIT = 'rate_limit',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  PARSE = 'parse',
  UNKNOWN = 'unknown',
}

/**
 * Base metadata attached to all application errors for Sentry context.
 */
interface IBaseErrorMetadata {
  /** Module or feature where the error originated */
  module?: string;
  /** Component that triggered the error */
  component?: string;
  /** User action that led to the error */
  action?: string;
  /** Timestamp of the error occurrence */
  timestamp: number;
}

/**
 * Base application error class that all typed errors extend.
 * Provides structured metadata for Sentry reporting and consistent error handling.
 *
 * @example
 * throw new AppError('Something unexpected happened', {
 *   severity: EErrorSeverity.MEDIUM,
 *   category: EErrorCategory.UNKNOWN,
 * });
 */
export class AppError extends Error {
  /** Error severity for alerting and prioritization */
  readonly severity: EErrorSeverity;
  /** Error category for grouping and filtering */
  readonly category: EErrorCategory;
  /** Whether the error is recoverable by the user */
  readonly isRecoverable: boolean;
  /** Structured metadata for Sentry context */
  readonly metadata: IBaseErrorMetadata;
  /** Original error that caused this one */
  readonly cause?: unknown;

  constructor(
    message: string,
    options: {
      severity?: EErrorSeverity;
      category?: EErrorCategory;
      isRecoverable?: boolean;
      metadata?: Partial<IBaseErrorMetadata>;
      cause?: unknown;
    } = {},
  ) {
    super(message);
    this.name = 'AppError';
    this.severity = options.severity ?? EErrorSeverity.MEDIUM;
    this.category = options.category ?? EErrorCategory.UNKNOWN;
    this.isRecoverable = options.isRecoverable ?? true;
    this.cause = options.cause;
    this.metadata = {
      timestamp: Date.now(),
      ...options.metadata,
    };
  }

  /**
   * Returns Sentry-compatible tags for this error.
   */
  toSentryTags(): Record<string, string> {
    return {
      errorCategory: this.category,
      errorSeverity: this.severity,
      errorRecoverable: String(this.isRecoverable),
      ...(this.metadata.module ? { module: this.metadata.module } : {}),
      ...(this.metadata.component
        ? { component: this.metadata.component }
        : {}),
      ...(this.metadata.action ? { action: this.metadata.action } : {}),
    };
  }

  /**
   * Returns Sentry-compatible extra context for this error.
   */
  toSentryExtra(): Record<string, unknown> {
    return {
      metadata: this.metadata,
      isRecoverable: this.isRecoverable,
      ...(this.cause ? { originalError: String(this.cause) } : {}),
    };
  }
}

/**
 * Network-related errors (connection failures, DNS, CORS).
 *
 * @example
 * throw new NetworkError('Failed to connect to server');
 */
export class NetworkError extends AppError {
  constructor(
    message = 'Network connection failed',
    options: {
      metadata?: Partial<IBaseErrorMetadata>;
      cause?: unknown;
    } = {},
  ) {
    super(message, {
      severity: EErrorSeverity.HIGH,
      category: EErrorCategory.NETWORK,
      isRecoverable: true,
      ...options,
    });
    this.name = 'NetworkError';
  }
}

/**
 * API response errors with HTTP status code context.
 *
 * @example
 * throw new ApiError('Failed to create resource', { statusCode: 500, endpoint: '/api/v1/resources' });
 */
export class ApiError extends AppError {
  /** HTTP status code from the API response */
  readonly statusCode: number;
  /** API endpoint that returned the error */
  readonly endpoint?: string;
  /** Request ID for correlation with backend logs */
  readonly requestId?: string;

  constructor(
    message: string,
    options: {
      statusCode: number;
      endpoint?: string;
      requestId?: string;
      metadata?: Partial<IBaseErrorMetadata>;
      cause?: unknown;
    },
  ) {
    super(message, {
      severity:
        options.statusCode >= 500 ? EErrorSeverity.HIGH : EErrorSeverity.MEDIUM,
      category: EErrorCategory.API,
      isRecoverable: options.statusCode < 500,
      metadata: options.metadata,
      cause: options.cause,
    });
    this.name = 'ApiError';
    this.statusCode = options.statusCode;
    this.endpoint = options.endpoint;
    this.requestId = options.requestId;
  }

  override toSentryTags(): Record<string, string> {
    return {
      ...super.toSentryTags(),
      httpStatus: String(this.statusCode),
      ...(this.endpoint ? { endpoint: this.endpoint } : {}),
    };
  }

  override toSentryExtra(): Record<string, unknown> {
    return {
      ...super.toSentryExtra(),
      statusCode: this.statusCode,
      endpoint: this.endpoint,
      requestId: this.requestId,
    };
  }
}

/**
 * Authentication errors (expired tokens, invalid credentials).
 *
 * @example
 * throw new AuthenticationError('Session expired');
 */
export class AuthenticationError extends AppError {
  constructor(
    message = 'Authentication failed',
    options: {
      metadata?: Partial<IBaseErrorMetadata>;
      cause?: unknown;
    } = {},
  ) {
    super(message, {
      severity: EErrorSeverity.HIGH,
      category: EErrorCategory.AUTHENTICATION,
      isRecoverable: true,
      ...options,
    });
    this.name = 'AuthenticationError';
  }
}

/**
 * Authorization errors (403 Forbidden, insufficient permissions).
 *
 * @example
 * throw new AuthorizationError('Insufficient permissions for this workspace');
 */
export class AuthorizationError extends AppError {
  constructor(
    message = 'Access denied',
    options: {
      metadata?: Partial<IBaseErrorMetadata>;
      cause?: unknown;
    } = {},
  ) {
    super(message, {
      severity: EErrorSeverity.MEDIUM,
      category: EErrorCategory.AUTHORIZATION,
      isRecoverable: false,
      ...options,
    });
    this.name = 'AuthorizationError';
  }
}

/**
 * Validation errors for form data or API input.
 *
 * @example
 * throw new ValidationError('Invalid email format', { field: 'email' });
 */
export class ValidationError extends AppError {
  /** The field that failed validation */
  readonly field?: string;
  /** Validation constraint that was violated */
  readonly constraint?: string;

  constructor(
    message: string,
    options: {
      field?: string;
      constraint?: string;
      metadata?: Partial<IBaseErrorMetadata>;
      cause?: unknown;
    } = {},
  ) {
    super(message, {
      severity: EErrorSeverity.LOW,
      category: EErrorCategory.VALIDATION,
      isRecoverable: true,
      metadata: options.metadata,
      cause: options.cause,
    });
    this.name = 'ValidationError';
    this.field = options.field;
    this.constraint = options.constraint;
  }

  override toSentryExtra(): Record<string, unknown> {
    return {
      ...super.toSentryExtra(),
      ...(this.field ? { field: this.field } : {}),
      ...(this.constraint ? { constraint: this.constraint } : {}),
    };
  }
}

/**
 * Timeout errors for operations exceeding time limits.
 *
 * @example
 * throw new TimeoutError('API request timed out', { timeoutMs: 30000, operation: 'fetchUsers' });
 */
export class TimeoutError extends AppError {
  /** Timeout duration in milliseconds */
  readonly timeoutMs: number;
  /** Name of the operation that timed out */
  readonly operation?: string;

  constructor(
    message = 'Operation timed out',
    options: {
      timeoutMs?: number;
      operation?: string;
      metadata?: Partial<IBaseErrorMetadata>;
      cause?: unknown;
    } = {},
  ) {
    super(message, {
      severity: EErrorSeverity.MEDIUM,
      category: EErrorCategory.TIMEOUT,
      isRecoverable: true,
      metadata: options.metadata,
      cause: options.cause,
    });
    this.name = 'TimeoutError';
    this.timeoutMs = options.timeoutMs ?? 0;
    this.operation = options.operation;
  }

  override toSentryExtra(): Record<string, unknown> {
    return {
      ...super.toSentryExtra(),
      timeoutMs: this.timeoutMs,
      ...(this.operation ? { operation: this.operation } : {}),
    };
  }
}

/**
 * Rate limit errors (429 Too Many Requests).
 *
 * @example
 * throw new RateLimitError('Too many requests', { retryAfterMs: 60000 });
 */
export class RateLimitError extends AppError {
  /** Time in milliseconds before the client should retry */
  readonly retryAfterMs?: number;

  constructor(
    message = 'Rate limit exceeded',
    options: {
      retryAfterMs?: number;
      metadata?: Partial<IBaseErrorMetadata>;
      cause?: unknown;
    } = {},
  ) {
    super(message, {
      severity: EErrorSeverity.LOW,
      category: EErrorCategory.RATE_LIMIT,
      isRecoverable: true,
      metadata: options.metadata,
      cause: options.cause,
    });
    this.name = 'RateLimitError';
    this.retryAfterMs = options.retryAfterMs;
  }

  override toSentryExtra(): Record<string, unknown> {
    return {
      ...super.toSentryExtra(),
      retryAfterMs: this.retryAfterMs,
    };
  }
}

/**
 * Parse errors for malformed JSON, protobuf, or other data formats.
 *
 * @example
 * throw new ParseError('Invalid JSON response', { rawData: '...' });
 */
export class ParseError extends AppError {
  constructor(
    message = 'Failed to parse data',
    options: {
      metadata?: Partial<IBaseErrorMetadata>;
      cause?: unknown;
    } = {},
  ) {
    super(message, {
      severity: EErrorSeverity.MEDIUM,
      category: EErrorCategory.PARSE,
      isRecoverable: false,
      ...options,
    });
    this.name = 'ParseError';
  }
}
